import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useScroll, useSpring } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { AnchorLink } from './Reveal';

const LINKS = [
  { label: 'Home', href: '#home', testid: 'nav-home-link' },
  { label: 'About Us', href: '#about', testid: 'nav-about-link' },
  { label: 'Core Services', href: '#services', testid: 'nav-services-link' },
  { label: 'Our Ecosystem', href: '#ecosystem', testid: 'nav-ecosystem-link' },
  { label: 'Upcoming Apps', href: '#digital-platforms', testid: 'nav-platforms-link' },
  { label: 'Industries', href: '#who-we-support', testid: 'nav-industries-link' },
  { label: 'Careers', href: '#for-job-seekers', testid: 'nav-careers-link' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 26, mass: 0.3 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-10 z-[60] border-b transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? 'border-slate-200/90 bg-white/90 shadow-[0_12px_32px_-20px_rgba(12,20,64,0.35)] backdrop-blur-xl'
          : 'border-transparent bg-white/70 backdrop-blur-md'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:h-[72px] lg:px-8">
        <AnchorLink href="#home" testid="header-logo-link" className="flex items-center gap-3" onNavigate={() => setOpen(false)}>
          <img
            src="/assets/sania-logo.png"
            alt="Sania Job Bowl — official logo"
            className="h-9 w-auto lg:h-10"
          />
        </AnchorLink>

        <nav className="hidden items-center gap-6 xl:gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <AnchorLink
              key={l.href}
              href={l.href}
              testid={l.testid}
              className="link-slide text-[13px] font-semibold tracking-wide text-slate-600 transition-colors duration-200 hover:text-royal"
            >
              {l.label}
            </AnchorLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+916361186321"
            data-testid="header-phone-link"
            className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-[13px] font-bold text-royal shadow-sm transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-royal/40 md:inline-flex"
          >
            <Phone className="h-3.5 w-3.5" aria-hidden="true" />
            +91 63611 86321
          </a>
          <AnchorLink
            href="#contact"
            testid="header-cta-link"
            className="hidden rounded-full bg-royal px-5 py-2.5 font-display text-[13px] font-bold uppercase tracking-[0.12em] text-white shadow-[0_10px_24px_-10px_rgba(45,58,151,0.6)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-royal-dark sm:inline-block"
          >
            Share Requirement →
          </AnchorLink>
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-royal shadow-sm lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <nav className="space-y-1 px-4 py-4" aria-label="Mobile">
              {LINKS.map((l) => (
                <AnchorLink
                  key={l.href}
                  href={l.href}
                  testid={`${l.testid}-mobile`}
                  onNavigate={() => setOpen(false)}
                  className="block rounded-xl px-4 py-3 font-display text-base font-bold text-ink transition-colors duration-200 hover:bg-porcelain hover:text-royal"
                >
                  {l.label}
                </AnchorLink>
              ))}
              <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-4 px-1">
                <a href="tel:+916361186321" data-testid="mobile-phone-link" className="text-sm font-bold text-royal">
                  +91 63611 86321
                </a>
                <a href="mailto:connect@saniajobbowl.com" data-testid="mobile-email-link" className="text-sm font-semibold text-slate-500">
                  connect@saniajobbowl.com
                </a>
                <AnchorLink
                  href="#contact"
                  testid="mobile-cta-link"
                  onNavigate={() => setOpen(false)}
                  className="mt-2 rounded-full bg-royal px-5 py-3 text-center font-display text-sm font-bold uppercase tracking-[0.12em] text-white"
                >
                  Share Requirement →
                </AnchorLink>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        data-testid="reading-progress-bar"
        aria-hidden="true"
        style={{ scaleX: progress }}
        className="absolute inset-x-0 bottom-0 h-[3px] origin-left bg-lime"
      />
    </header>
  );
}
